import { FC, useMemo, useState } from 'react';

import { DynamicAction } from '@shared/types/app';
import { PublicEntertainer } from '@shared/types/event';
import dayjs from 'dayjs';
import { uniqBy } from 'lodash';
import Markdown from 'markdown-to-jsx';

import { CurrentEntertainer, useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import { FeedEvent, useCurrentUserEventRsvp } from '@queries/EventQueries';
import { useCurrentUserEventCritiqueQuery } from '@queries/EventQueries';

import { useDynamicActionMutation } from '@mutations/AppMutations';
import {
  CritiqueDocumentMutationPayload,
  useCritiqueDocumentMutation,
} from '@mutations/CritiqueMutations';
import { useRsvpToEventMutation } from '@mutations/EventMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useDynamicAction } from '@hooks/useDynamicAction';

import { useNavigate } from '@router/index';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { Chip } from '@components/chips/Chip';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { LineAwesomeIcon } from '@components/icons/LineAwesomeIcon';
import { PopoverWrapper } from '@components/layout/Popover/PopoverWrapper';
import { SeoLink } from '@components/layout/SeoLink/SeoLink';

import { EntertainerScheduleDateDetail } from '@screens/entertainer/DragKingCommunityScreen/ScheduledSocialPost';

import fireIconUrl from '../assets/fire.svg';

const IS_MEDIA_ENABLED = false;
const IS_TICKET_BUTTON_ENABLED = true;

export interface ShowListItemSuggestion {
  payload: Pick<CritiqueDocumentMutationPayload, 'like'>;
  iconUrl: string;
  iconBorderRadius?: number;
}

interface SharedIconButtonProps {
  event: FeedEvent;
  updateActionChips: (updatedChips: FeedEvent['chips']) => void;
}

const SuggestionIconButton: FC<
  SharedIconButtonProps & {
    suggestion: ShowListItemSuggestion;
  }
> = ({ event, suggestion, updateActionChips }) => {
  const { userEventCritique, refetchUserEventCritique } = useCurrentUserEventCritiqueQuery(
    event._id,
  );
  const userDidSuggest = suggestion?.payload?.like?.every((like) =>
    userEventCritique?.like?.includes(like),
  );

  const { critiqueDocumentAsync } = useCritiqueDocumentMutation();
  const handleSuggestion = async () => {
    const response = await critiqueDocumentAsync({
      documentId: event._id,
      documentType: 'events',
      like: userDidSuggest
        ? (userEventCritique?.like?.filter((like) => !suggestion?.payload?.like?.includes(like)) ??
          [])
        : [...(userEventCritique?.like ?? []), ...(suggestion?.payload?.like ?? [])],
    });

    updateActionChips(response?.event?.chips ?? []);

    refetchUserEventCritique();
  };

  return (
    <IconButton className="" variant="smooth" color="neutral" onClick={handleSuggestion}>
      <img
        src={suggestion?.iconUrl}
        alt="Suggestion Icon"
        style={{
          width: 20,
          height: 20,
          borderRadius: suggestion?.iconBorderRadius,
          display: 'block',
          filter: userDidSuggest ? undefined : 'grayscale(1)',
          objectFit: 'contain',
        }}
      />
    </IconButton>
  );
};

const HypeEventIconButton: FC<SharedIconButtonProps> = ({ event, updateActionChips }) => {
  const { rsvp } = useCurrentUserEventRsvp(event._id);
  const { rsvpToEventAsync } = useRsvpToEventMutation();
  const userDidHype = rsvp?.eventInterest === 'hyped';

  const handleHypeEvent = async () => {
    const response = await rsvpToEventAsync({
      eventId: event._id,
      eventInterest: userDidHype ? null : 'hyped',
    });

    updateActionChips(response?.event?.chips ?? []);
  };

  return (
    <IconButton className="" variant="smooth" color="neutral" onClick={handleHypeEvent}>
      <img
        src={fireIconUrl}
        alt="Fire Icon"
        style={{
          width: 20,
          height: 20,
          display: 'block',
          filter: userDidHype ? undefined : 'grayscale(1)',
        }}
      />
    </IconButton>
  );
};

const PastEventMenuActionButton: FC<{ event: FeedEvent }> = ({ event }) => {
  const { track } = useAnalytics();
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const { handleDynamicAction } = useDynamicAction();

  const shouldShowRecapShow = event?.entertainers
    ?.map(({ _id }) => _id)
    ?.includes(currentEntertainer?._id);

  const now = dayjs();
  const menuActions = useMemo(
    () =>
      [
        {
          isVisible: shouldShowRecapShow,
          text: 'Recap Show',
          badgeCount: 1,
          onClick: () => {
            track('Recap Show Clicked', { eventId: event._id });
            handleDynamicAction({
              action: DynamicAction.SplashNotificationRecapShow,
              payload: { eventId: event._id },
            });
          },
        },
      ].filter(({ isVisible }) => isVisible && !now.isAfter(dayjs(event.startDate).add(7, 'days'))),
    [currentEntertainer?._id, event, shouldShowRecapShow],
  );

  if (!menuActions.length) {
    return <></>;
  }

  return (
    <PopoverWrapper
      PopoverContent={({ onClose }) => (
        <div>
          {menuActions.map(({ text, badgeCount, onClick }) => (
            <Button
              key={text}
              className="w-100"
              variant="flat"
              color="neutral"
              chip={badgeCount ? { text: badgeCount } : undefined}
              onClick={async () => {
                await onClick();
                onClose();
              }}
            >
              {text}
            </Button>
          ))}
        </div>
      )}
    >
      <IconButton
        data-testid="show-list-item-cta"
        variant="smooth"
        color="neutral"
        badge={shouldShowRecapShow}
      >
        <LineAwesomeIcon name="ellipsis-h" />
      </IconButton>
    </PopoverWrapper>
  );
};

export const ShowListItem = ({
  event,
  onNavigate,
  onJoin,
  suggestion,
  hideEntertainerMenuButton,
  onAddExternalLink,
}: {
  event: FeedEvent;
  onNavigate: () => void;
  onJoin: (cb: (createdEntertainer: Pick<CurrentEntertainer, '_id' | 'title'>) => void) => void;
  suggestion?: ShowListItemSuggestion;
  hideEntertainerMenuButton?: boolean;
  onAddExternalLink?: (show: FeedEvent | EntertainerScheduleDateDetail) => void;
}) => {
  const navigate = useNavigate();
  const { entertainer } = useCurrentEntertainerQuery();
  const [modifiedEntertainers, setModifiedEntertainers] = useState<
    Pick<PublicEntertainer, '_id' | 'title'>[]
  >(event.entertainers ?? []);
  const [actionChips, setActionChips] = useState<FeedEvent['chips']>([]);

  const chips = useMemo(() => {
    return uniqBy(
      [...(actionChips ?? []), ...(event.chips ?? [])],
      ({ startIcon, label }) => `${startIcon}-${label}`,
    );
  }, [event.chips, actionChips]);

  const handleJoinCallback = async (
    createdEntertainer: Pick<CurrentEntertainer, '_id' | 'title'>,
  ) => {
    const addedEntertainer = createdEntertainer ?? entertainer;
    if (addedEntertainer?._id) {
      setModifiedEntertainers((prevValue) => [
        ...prevValue,
        { _id: addedEntertainer._id, title: addedEntertainer.title },
      ]);
    }
  };

  const linkUrl = `https://gaggl.app/events/${event._id}/reviews`;

  const isPastEvent = dayjs(event.startDate).isBefore(dayjs(), 'day');

  return (
    <li className="media-item">
      <div className="item-content" style={IS_MEDIA_ENABLED ? { paddingLeft: 0 } : undefined}>
        {IS_MEDIA_ENABLED && (
          <div
            slot="media"
            style={{
              width: 72,
              textAlign: 'center',
              alignSelf: 'flex-start',
              flexShrink: 0,
              paddingTop: 'var(--f7-list-item-padding-vertical)',
            }}
          >
            <div>
              {event.img ? (
                <img src={event.img} alt={event.title} style={{ width: 48, borderRadius: 4 }} />
              ) : (
                <div style={{ lineHeight: 1 }}>
                  <div style={{ fontWeight: 600 }}>{dayjs(event.startDate).format('D')}</div>
                  <div style={{ fontSize: 12 }}>{dayjs(event.startDate).format('ddd')}</div>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="item-inner">
          <div className="d-flex align-items-center">
            <div style={{ overflow: 'hidden' }}>
              <SeoLink
                href={linkUrl}
                className="item-text"
                style={{ fontSize: 14, lineHeight: '1.1' }}
                onClick={onNavigate}
              >
                {event.timeDetails}
              </SeoLink>
              <div className="item-title-row">
                <SeoLink href={linkUrl} className="item-title" onClick={onNavigate}>
                  {event.title}
                </SeoLink>
              </div>
              <div className="item-footer">
                <div className="d-grid gap-1">
                  {hideEntertainerMenuButton}
                  <div>
                    {modifiedEntertainers?.length || hideEntertainerMenuButton
                      ? modifiedEntertainers?.map(({ title }) => title).join(', ')
                      : 'No entertainers added'}
                  </div>
                  <div>
                    <div className="d-flex gap-2">
                      {IS_TICKET_BUTTON_ENABLED &&
                        !isPastEvent &&
                        (event?.externalLinkButton?.url ? (
                          <div style={{ padding: '2px 0' }}>
                            <Button
                              variant="smooth"
                              color="yellow"
                              size="sm"
                              className="px-2"
                              style={{ borderRadius: 4, height: 24 }}
                              endIcon={
                                <LineAwesomeIcon
                                  name="external-link-alt"
                                  style={{ fontSize: 16, lineHeight: 1 }}
                                />
                              }
                              onClick={() => navigate(event?.externalLinkButton?.url)}
                            >
                              {event?.externalLinkButton?.text ?? 'Tickets'}
                            </Button>
                          </div>
                        ) : (
                          onAddExternalLink && (
                            <div style={{ padding: '2px 0' }}>
                              <Button
                                variant="outlined dashed"
                                color="yellow"
                                size="sm"
                                className="px-2"
                                style={{
                                  borderRadius: 4,
                                  height: 24,
                                  background: 'rgba(255, 204, 0, 0.1)',
                                }}
                                startIcon={<FontAwesomeIcon name="plus" />}
                                onClick={() => onAddExternalLink?.(event)}
                              >
                                Ticket Link
                              </Button>
                            </div>
                          )
                        ))}
                      {!!chips.length && (
                        <div className="d-flex flex-wrap gap-1">
                          {chips.map((chip) => (
                            <Chip
                              key={chip.label}
                              label={
                                <div className="d-flex align-items-center gap-1">
                                  <Markdown
                                    options={{ forceBlock: true, forceWrapper: true }}
                                    style={{ fontSize: 14 }}
                                  >
                                    {chip.startIcon}
                                  </Markdown>
                                  <div>{chip.label}</div>
                                </div>
                              }
                              color="neutral"
                              size="sm"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="ml-auto ps-1">
              <div className="d-flex gap-1 align-items-center">
                <div>
                  {isPastEvent ? (
                    hideEntertainerMenuButton && <PastEventMenuActionButton event={event} />
                  ) : suggestion ? (
                    <SuggestionIconButton
                      event={event}
                      suggestion={suggestion}
                      updateActionChips={setActionChips}
                    />
                  ) : (
                    <HypeEventIconButton event={event} updateActionChips={setActionChips} />
                  )}
                </div>
                {!hideEntertainerMenuButton && (
                  <div>
                    <PopoverWrapper
                      PopoverContent={({ onClose }) => (
                        <div>
                          <Button
                            className="w-100"
                            variant="flat"
                            color="neutral"
                            onClick={() => {
                              onClose();
                              onJoin(handleJoinCallback);
                            }}
                          >
                            Join Lineup
                          </Button>
                        </div>
                      )}
                    >
                      <IconButton data-testid="show-list-item-cta" variant="smooth" color="neutral">
                        <LineAwesomeIcon name="ellipsis-h" />
                      </IconButton>
                    </PopoverWrapper>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

export const EntertainerScheduleDateDetailListItem = ({
  entertainerScheduleDateDetail,
  onClick,
}: {
  entertainerScheduleDateDetail: Omit<EntertainerScheduleDateDetail, 'entertainer'> &
    Partial<Pick<EntertainerScheduleDateDetail, 'entertainer'>>;
  onClick: () => void;
}) => {
  const { entertainer, date, dateDetails } = entertainerScheduleDateDetail;
  const linkUrl = '';

  if (!dateDetails.showName) {
    return <></>;
  }

  return (
    <li className="media-item">
      <div className="item-content">
        <div className="item-inner">
          <div className="d-flex align-items-center">
            <div onClick={onClick} style={{ overflow: 'hidden' }}>
              <span className="item-text" style={{ fontSize: 14, lineHeight: '1.1' }}>
                {dayjs(date).format('dddd, MMM Do')}
              </span>
              <div className="item-title-row">
                <SeoLink href={linkUrl} className="item-title">
                  {dateDetails.showName}
                </SeoLink>
              </div>
              {entertainer && (
                <div className="item-footer">
                  <div className="d-grid gap-1">
                    <div>{entertainer.title}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};
